import { getApiErrorPresentation, type ApiErrorPresentation } from '../errors/apiErrorPresentation';

export type HeaderValue = string | undefined;
export type HttpHeaderRecord = Record<string, HeaderValue>;

export interface HeaderController {
  set(name: string, value: string): void;
  delete(name: string): void;
}

export type HttpHeaders = HeaderController | HttpHeaderRecord;

export interface HttpRequestConfig {
  url?: string;
  method?: string;
  headers?: HttpHeaders;
  params?: Record<string, unknown>;
  data?: unknown;
  timeout?: number;
  baseURL?: string;
  [key: string]: unknown;
}

export interface HttpClientConfig {
  baseURL: string;
  timeout: number;
  headers?: HttpHeaderRecord;
  withCredentials?: boolean;
  xsrfCookieName?: string;
  xsrfHeaderName?: string;
}

export interface HttpResponse<T = unknown> {
  data: T;
  status?: number;
  statusText?: string;
  config?: HttpRequestConfig;
}

export interface HttpInterceptorManager<T> {
  use(
    onFulfilled: (value: T) => T | Promise<T>,
    onRejected?: (error: unknown) => unknown
  ): unknown;
}

export interface HttpClientInstance {
  (config: HttpRequestConfig): Promise<HttpResponse>;
  request(config: HttpRequestConfig): Promise<HttpResponse>;
  get<T = unknown>(url: string, config?: { params?: Record<string, unknown> }): Promise<HttpResponse<T>>;
  post<T = unknown>(url: string, data?: unknown): Promise<HttpResponse<T>>;
  patch<T = unknown>(url: string, data?: unknown): Promise<HttpResponse<T>>;
  put<T = unknown>(url: string, data?: unknown): Promise<HttpResponse<T>>;
  delete(url: string): Promise<HttpResponse<unknown>>;
  defaults: {
    headers: {
      common: HttpHeaderRecord;
    };
  };
  interceptors: {
    request: HttpInterceptorManager<HttpRequestConfig>;
    response: HttpInterceptorManager<HttpResponse>;
  };
}

export interface TokenStorageAdapter {
  getAuthHeader(): string | null;
  needsRefresh?(): boolean;
  refreshAccessToken?(): Promise<string | null>;
  clearAuth?(): void | Promise<void>;
  isUsingJwt?(): boolean;
  /**
   * Returns true when the user should be authenticated (e.g. user record exists
   * in storage) but `getAuthHeader()` returns null — used to detect the silent
   * "token cleared after failed refresh" broken state and force a re-login.
   */
  shouldBeAuthenticated?(): boolean;
}

export interface TenantContextAdapter {
  getTenantId(): string | null | undefined;
  /**
   * Clears the stored tenant context (tenantId/tenantName/tenantSlug).
   * Called by the response interceptor when a stale X-Tenant-ID causes a 403
   * so the next request falls back to the backend's membership-lookup path.
   */
  clearTenantId?(): void;
}

export interface NavigationAdapter {
  handleAuthFailure?(presentation: ApiErrorPresentation): void | Promise<void>;
  handleAuthRefreshFailure?(presentation: ApiErrorPresentation): void | Promise<void>;
}

export interface BaseApiClientLogger {
  debug?(message: string, meta?: unknown): void;
  warn?(message: string, meta?: unknown): void;
  error?(message: string, meta?: unknown): void;
}

export interface CircuitBreakerErrorArgs {
  friendlyMessage: string;
  status: number;
  request?: {
    method?: string;
    url?: string;
    baseURL?: string;
  };
  responseData?: unknown;
  originalError?: unknown;
}

export interface ServerErrorReportArgs extends CircuitBreakerErrorArgs {
  error: unknown;
}

export interface BaseApiClientOptions {
  clientName: string;
  createClient(config: HttpClientConfig): HttpClientInstance;
  baseURL: string;
  timeout: number;
  defaultHeaders?: HttpHeaderRecord;
  withCredentials?: boolean;
  xsrfCookieName?: string;
  xsrfHeaderName?: string;
  tokenStorage: TokenStorageAdapter;
  tenantContext?: TenantContextAdapter;
  navigation?: NavigationAdapter;
  logger?: BaseApiClientLogger;
  authEndpoints?: string[];
  refreshTimeoutMs?: number;
  maxAuthRetries?: number;
  retry?: {
    maxRetries?: number;
    baseDelayMs?: number;
    timeoutMsOnRetry?: number;
    retryableStatuses?: number[];
  };
  loadingHooks?: {
    onRequestStart?(): void;
    onRequestEnd?(): void;
  };
  circuitBreaker?: {
    onTrip?(): void;
    createError?(args: CircuitBreakerErrorArgs): Error;
    report?(args: ServerErrorReportArgs): void;
  };
}

export interface ManagedApiClient {
  client: HttpClientInstance;
  get<T = unknown>(path: string, params?: Record<string, unknown>): Promise<T>;
  post<T = unknown>(path: string, data?: unknown): Promise<T>;
  patch<T = unknown>(path: string, data?: unknown): Promise<T>;
  put<T = unknown>(path: string, data?: unknown): Promise<T>;
  delete(path: string): Promise<void>;
  resetAuthRefreshState(): void;
}

interface RequestRetryConfig extends HttpRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
  _sharedRetryCount?: number;
  _timeoutRetry?: boolean;
  /** Set on the one allowed stale-tenant retry to prevent infinite loops. */
  _staleTenantRetry?: boolean;
}

interface QueuedRequest {
  config: RequestRetryConfig;
  resolve(config: RequestRetryConfig): void;
  reject(error: unknown): void;
}

const DEFAULT_AUTH_ENDPOINTS = [
  '/auth/token/refresh/',
  '/auth/token/verify/',
  '/auth/token/',
  '/auth/login/',
  '/auth/logout/',
  '/auth/guest-login/',
  '/auth/signup',
  '/invitations/validate/',
];

function isHeaderController(headers: HttpHeaders | undefined): headers is HeaderController {
  return (
    !!headers
    && typeof headers === 'object'
    && 'set' in headers
    && 'delete' in headers
    && typeof (headers as HeaderController).set === 'function'
    && typeof (headers as HeaderController).delete === 'function'
  );
}

function setHeader(headers: HttpHeaders | undefined, name: string, value: string): void {
  if (!headers) return;
  if (isHeaderController(headers)) {
    headers.set(name, value);
    return;
  }

  headers[name] = value;
}

function deleteHeader(headers: HttpHeaders | undefined, ...names: string[]): void {
  if (!headers) return;
  if (isHeaderController(headers)) {
    names.forEach((name) => headers.delete(name));
    return;
  }

  names.forEach((name) => {
    delete headers[name];
  });
}

function hasHeader(headers: HttpHeaders | undefined, name: string): boolean {
  if (!headers) return false;
  if (isHeaderController(headers)) {
    // HeaderController has no 'has' method; check by reading via the record path
    // after narrowing fails — use the record cast as a safe fallback.
    return Object.prototype.hasOwnProperty.call(headers, name) && !!(headers as unknown as Record<string, unknown>)[name];
  }
  return Object.prototype.hasOwnProperty.call(headers, name) && !!headers[name];
}

function stripJsonContentTypeForFormData(config: HttpRequestConfig): void {
  if (typeof FormData === 'undefined') return;
  if (!(config.data instanceof FormData)) return;
  deleteHeader(config.headers, 'Content-Type', 'content-type');
}

function isAuthEndpoint(url: string | undefined, authEndpoints: string[]): boolean {
  const candidate = String(url ?? '');
  return authEndpoints.some((path) => candidate.includes(path));
}

function getErrorConfig(error: unknown): RequestRetryConfig | undefined {
  if (!error || typeof error !== 'object') return undefined;
  return (error as { config?: RequestRetryConfig }).config;
}

function getErrorResponseData(error: unknown): unknown {
  if (!error || typeof error !== 'object') return undefined;
  return (error as { response?: { data?: unknown } }).response?.data;
}

function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  return (error as { response?: { status?: number } }).response?.status;
}

function getErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  return (error as { code?: string }).code;
}

function getErrorMessageText(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return '';
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isIdempotentRequest(config: RequestRetryConfig | undefined): boolean {
  const method = String(config?.method ?? 'get').toLowerCase();
  return ['get', 'head', 'options'].includes(method);
}

function getRequestPath(config: RequestRetryConfig | undefined): string {
  const url = String(config?.url ?? '').split('?')[0];
  return url || 'unknown path';
}

function getRequestMethod(config: RequestRetryConfig | undefined): string {
  return String(config?.method ?? 'GET').toUpperCase();
}

function buildTimeoutFriendlyMessage(requestConfig: RequestRetryConfig | undefined): string {
  const method = getRequestMethod(requestConfig);
  const path = getRequestPath(requestConfig);
  return `Request timed out (${method} ${path}). The server did not answer in time.`;
}

function buildNetworkFriendlyMessage(requestConfig: RequestRetryConfig | undefined): string {
  const method = getRequestMethod(requestConfig);
  const path = getRequestPath(requestConfig);
  return `Unable to reach the server (${method} ${path}). Please check your connection and try again.`;
}

/** Deliberate API error text ({ error, code }) -- never proxy/HTML bodies. */
function getBackendErrorMessage(error: unknown): string | undefined {
  const data = getErrorResponseData(error);
  if (!data || typeof data !== 'object') return undefined;
  const { error: message, code } = data as { error?: unknown; code?: unknown };
  return typeof message === 'string' && message.trim() && typeof code === 'string' ? message : undefined;
}

function buildServerFriendlyMessage(status: number): string {
  if (status === 504) {
    return 'Server is not responding — it may be restarting. Please try again shortly.';
  }
  if (status === 502) {
    return 'Server temporarily unreachable. Please try again shortly.';
  }
  return 'Server error. Please try again shortly.';
}

function replayRequest(
  client: HttpClientInstance,
  config: RequestRetryConfig
): Promise<HttpResponse> {
  if (typeof client === 'function') {
    return client(config);
  }
  return (client as { request(config: RequestRetryConfig): Promise<HttpResponse> }).request(config);
}

/**
 * Creates a platform-agnostic managed API client with tenant headers, auth refresh,
 * retry/backoff, and optional circuit-breaker hooks.
 */
export function createBaseApiClient(options: BaseApiClientOptions): ManagedApiClient {
  const client = options.createClient({
    baseURL: options.baseURL,
    timeout: options.timeout,
    headers: options.defaultHeaders,
    withCredentials: options.withCredentials,
    xsrfCookieName: options.xsrfCookieName,
    xsrfHeaderName: options.xsrfHeaderName,
  });

  const authEndpoints = options.authEndpoints ?? DEFAULT_AUTH_ENDPOINTS;
  const retryOptions = {
    maxRetries: options.retry?.maxRetries ?? 1,
    baseDelayMs: options.retry?.baseDelayMs ?? 250,
    timeoutMsOnRetry: options.retry?.timeoutMsOnRetry ?? 45_000,
    retryableStatuses: options.retry?.retryableStatuses ?? [500, 502, 503, 504],
  };

  let isRefreshing = false;
  let isHandlingAuthFailure = false;
  let failedQueue: QueuedRequest[] = [];
  const recentServerErrors = new Map<string, number>();

  const shouldLogServerError = (url: string | undefined, status: number): boolean => {
    const key = `${status}:${url ?? 'unknown'}`;
    const now = Date.now();
    const lastSeen = recentServerErrors.get(key) ?? 0;
    if (now - lastSeen < 10_000) {
      return false;
    }

    recentServerErrors.set(key, now);
    if (recentServerErrors.size > 50) {
      recentServerErrors.forEach((seenAt, seenKey) => {
        if (now - seenAt > 30_000) {
          recentServerErrors.delete(seenKey);
        }
      });
    }
    return true;
  };

  const processQueue = (error: unknown | null): void => {
    failedQueue.forEach(({ config, resolve, reject }) => {
      if (error) {
        reject(error);
        return;
      }

      const authHeader = options.tokenStorage.getAuthHeader();
      if (authHeader) {
        setHeader(config.headers, 'Authorization', authHeader);
      }
      resolve(config);
    });
    failedQueue = [];
  };

  const runAuthHandler = async (
    kind: 'auth' | 'refresh',
    error: unknown,
    fallbackMessage: string
  ): Promise<void> => {
    const handler =
      kind === 'refresh'
        ? options.navigation?.handleAuthRefreshFailure ?? options.navigation?.handleAuthFailure
        : options.navigation?.handleAuthFailure;

    if (!handler || isHandlingAuthFailure) {
      return;
    }

    isHandlingAuthFailure = true;
    try {
      await handler(getApiErrorPresentation(error, { fallbackMessage }));
    } finally {
      isHandlingAuthFailure = false;
    }
  };

  const reportServerError = (
    error: unknown,
    status: number,
    friendlyMessage: string,
    requestConfig: RequestRetryConfig | undefined
  ): void => {
    options.circuitBreaker?.report?.({
      error,
      friendlyMessage,
      status,
      request: {
        method: requestConfig?.method,
        url: requestConfig?.url,
        baseURL: requestConfig?.baseURL,
      },
      responseData: getErrorResponseData(error),
      originalError: error,
    });
  };

  const createCircuitBreakerError = (
    error: unknown,
    status: number,
    friendlyMessage: string,
    requestConfig: RequestRetryConfig | undefined
  ): Error | unknown =>
    options.circuitBreaker?.createError?.({
      friendlyMessage,
      status,
      request: {
        method: requestConfig?.method,
        url: requestConfig?.url,
        baseURL: requestConfig?.baseURL,
      },
      responseData: getErrorResponseData(error),
      originalError: error,
    }) ?? error;

  const maybeRetry = async (
    requestConfig: RequestRetryConfig | undefined,
    reason: 'network' | 'server'
  ): Promise<HttpResponse | null> => {
    if (!requestConfig || !isIdempotentRequest(requestConfig)) {
      return null;
    }

    const retryCount = requestConfig._sharedRetryCount ?? 0;
    if (retryCount >= retryOptions.maxRetries) {
      return null;
    }

    requestConfig._sharedRetryCount = retryCount + 1;
    const delayMs = retryOptions.baseDelayMs * (2 ** retryCount);
    options.logger?.warn?.(`[${options.clientName}] Retrying request`, {
      url: requestConfig.url,
      method: requestConfig.method,
      reason,
      retryCount: requestConfig._sharedRetryCount,
      delayMs,
    });

    if (reason === 'network') {
      requestConfig._timeoutRetry = true;
      requestConfig.timeout = retryOptions.timeoutMsOnRetry;
    }

    await sleep(delayMs);
    return replayRequest(client, requestConfig);
  };

  client.interceptors.request.use(
    async (config) => {
      options.loadingHooks?.onRequestStart?.();
      stripJsonContentTypeForFormData(config);

      const authRequest = isAuthEndpoint(config.url, authEndpoints);
      if (
        !authRequest
        && options.tokenStorage.isUsingJwt?.()
        && options.tokenStorage.needsRefresh?.()
        && options.tokenStorage.refreshAccessToken
        && !isRefreshing
      ) {
        try {
          await Promise.race([
            options.tokenStorage.refreshAccessToken(),
            sleep(options.refreshTimeoutMs ?? 3_000).then(() => null),
          ]);
        } catch (refreshError) {
          options.logger?.debug?.(`[${options.clientName}] Token refresh preflight failed`, refreshError);
        }
      }

      const authHeader = !authRequest ? options.tokenStorage.getAuthHeader() : null;
      if (authHeader) {
        setHeader(config.headers, 'Authorization', authHeader);
      } else {
        deleteHeader(config.headers, 'Authorization', 'authorization');

        // Detect silent broken state: user should be authenticated but their
        // token is gone (cleared after a failed refresh).  Sending the request
        // without auth would return a silent 403 that never triggers the
        // re-auth flow.  Redirect to login immediately instead.
        if (
          !authRequest
          && !isHandlingAuthFailure
          && options.tokenStorage.shouldBeAuthenticated?.()
        ) {
          options.logger?.warn?.(
            `[${options.clientName}] No auth token but user appears logged in — forcing re-auth`
          );
          await runAuthHandler(
            'auth',
            new Error('Session token missing'),
            'Your session has expired. Please sign in again.'
          );
          return Promise.reject(new Error('Session expired'));
        }
      }

      const tenantId = options.tenantContext?.getTenantId();
      if (tenantId) {
        setHeader(config.headers, 'X-Tenant-ID', tenantId);
      } else {
        deleteHeader(config.headers, 'X-Tenant-ID', 'x-tenant-id');
      }

      return config;
    },
    (error) => {
      options.loadingHooks?.onRequestEnd?.();
      options.logger?.error?.(`[${options.clientName}] Request interceptor rejected`, error);
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    (response) => {
      options.loadingHooks?.onRequestEnd?.();
      return response;
    },
    async (error) => {
      options.loadingHooks?.onRequestEnd?.();

      const requestConfig = getErrorConfig(error);
      const status = getErrorStatus(error);
      const code = getErrorCode(error);
      const message = getErrorMessageText(error).toLowerCase();
      const isTimeout = code === 'ECONNABORTED' || message.includes('timeout');
      const isNetworkError = code === 'ERR_NETWORK';

      if ((isTimeout || isNetworkError) && !status) {
        const retryResponse = await maybeRetry(requestConfig, 'network');
        if (retryResponse) {
          return retryResponse;
        }

        const friendlyMessage = isTimeout
          ? buildTimeoutFriendlyMessage(requestConfig)
          : buildNetworkFriendlyMessage(requestConfig);

        if (shouldLogServerError(requestConfig?.url, 0)) {
          options.logger?.error?.(`[${options.clientName}] Request timeout or network error`, {
            url: requestConfig?.url,
            method: requestConfig?.method,
            timeoutMs: requestConfig?.timeout ?? options.timeout,
            code,
          });
        }

        options.circuitBreaker?.onTrip?.();
        return Promise.reject(
          createCircuitBreakerError(error, 0, friendlyMessage, requestConfig)
        );
      }

      if (typeof status === 'number' && retryOptions.retryableStatuses.includes(status)) {
        const retryResponse = await maybeRetry(requestConfig, 'server');
        if (retryResponse) {
          return retryResponse;
        }

        const friendlyMessage = getBackendErrorMessage(error) ?? buildServerFriendlyMessage(status);
        if (shouldLogServerError(requestConfig?.url, status)) {
          const meta = {
            status,
            url: requestConfig?.url,
            method: requestConfig?.method,
          };
          const message = `[${options.clientName}] Server error (circuit breaker)`;
          if (status === 502 || status === 503) options.logger?.warn?.(message, meta);
          else options.logger?.error?.(message, meta);
        }

        reportServerError(error, status, friendlyMessage, requestConfig);
        options.circuitBreaker?.onTrip?.();
        return Promise.reject(
          createCircuitBreakerError(error, status, friendlyMessage, requestConfig)
        );
      }

      if (status === 401 && requestConfig && isAuthEndpoint(requestConfig.url, authEndpoints)) {
        return Promise.reject(error);
      }

      if (status === 401 && requestConfig && options.tokenStorage.isUsingJwt?.()) {
        const retryCount = (requestConfig._retryCount ?? 0) + 1;
        if (retryCount > (options.maxAuthRetries ?? 2)) {
          await options.tokenStorage.clearAuth?.();
          await runAuthHandler('refresh', error, 'Your session has expired. Please sign in again.');
          return Promise.reject(error);
        }

        if (!options.tokenStorage.refreshAccessToken) {
          await runAuthHandler('auth', error, 'Your session has expired. Please sign in again.');
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise<RequestRetryConfig>((resolve, reject) => {
            failedQueue.push({ config: requestConfig, resolve, reject });
          }).then((queuedConfig) => replayRequest(client, queuedConfig));
        }

        requestConfig._retry = true;
        requestConfig._retryCount = retryCount;
        isRefreshing = true;

        try {
          const newToken = await options.tokenStorage.refreshAccessToken();
          if (!newToken) {
            throw new Error('Token refresh returned null');
          }

          setHeader(requestConfig.headers, 'Authorization', options.tokenStorage.getAuthHeader() ?? newToken);
          processQueue(null);
          return replayRequest(client, requestConfig);
        } catch (refreshError) {
          processQueue(refreshError);
          await options.tokenStorage.clearAuth?.();
          await runAuthHandler('refresh', refreshError, 'Your session has expired. Please sign in again.');
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      if (status === 401) {
        await runAuthHandler('auth', error, 'Your session has expired. Please sign in again.');
      }

      // Stale X-Tenant-ID: the request was sent with an explicit tenant header
      // that the backend rejected (user no longer a member, or a previous
      // user's tenantId bled into this session).  Clear the stored tenant and
      // retry ONCE — the second request omits X-Tenant-ID so the backend's
      // membership-lookup fallback resolves the correct tenant automatically.
      if (
        status === 403
        && requestConfig
        && !requestConfig._staleTenantRetry
        && hasHeader(requestConfig.headers, 'X-Tenant-ID')
        && options.tenantContext?.clearTenantId
      ) {
        options.logger?.warn?.(
          `[${options.clientName}] Stale X-Tenant-ID on ${requestConfig.url} — clearing tenant context and retrying`
        );
        options.tenantContext.clearTenantId();
        const retryConfig: RequestRetryConfig = { ...requestConfig, _staleTenantRetry: true };
        // Also strip the header directly from the cloned config so it is
        // definitely absent even if AxiosHeaders spread-copy behaves unexpectedly.
        deleteHeader(retryConfig.headers, 'X-Tenant-ID', 'x-tenant-id');
        return replayRequest(client, retryConfig);
      }

      // 403 when no Authorization header was sent means the request went out
      // unauthenticated (token was missing).  Treat it like a 401 auth failure
      // so the user is redirected to login rather than seeing a silent broken
      // state.
      if (
        status === 403
        && requestConfig
        && !isAuthEndpoint(requestConfig.url, authEndpoints)
        && !hasHeader(requestConfig.headers, 'Authorization')
        && !hasHeader(requestConfig.headers, 'authorization')
        && options.tokenStorage.shouldBeAuthenticated?.()
      ) {
        await runAuthHandler('auth', error, 'Your session has expired. Please sign in again.');
      }

      return Promise.reject(error);
    }
  );

  return {
    client,
    get: <T = unknown>(path: string, params?: Record<string, unknown>) =>
      client.get<T>(path, { params }).then((response) => response.data as T),
    post: <T = unknown>(path: string, data?: unknown) =>
      client.post<T>(path, data).then((response) => response.data as T),
    patch: <T = unknown>(path: string, data?: unknown) =>
      client.patch<T>(path, data).then((response) => response.data as T),
    put: <T = unknown>(path: string, data?: unknown) =>
      client.put<T>(path, data).then((response) => response.data as T),
    delete: (path) => client.delete(path).then(() => undefined),
    resetAuthRefreshState: () => {
      isRefreshing = false;
      isHandlingAuthFailure = false;
      failedQueue = [];
      recentServerErrors.clear();
    },
  };
}
